import React, {useContext, useState} from "react"
import useScrollTrigger from '@mui/material/useScrollTrigger';
import AppBar from '@mui/material/AppBar'
import Toolbar from '@mui/material/Toolbar'
import Typography from '@mui/material/Typography'
import IconButton from '@mui/material/Button'
import Button from '@mui/material/Button'
import {useTranslation} from "react-i18next"
import {Badge, Box, FormControl, Grid2, InputLabel, Link, Menu, MenuItem, Select, Slide, Tooltip} from '@mui/material'
import MenuIcon from '@mui/icons-material/Menu';
import PersonIcon from '@mui/icons-material/Person';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import LoginIcon from '@mui/icons-material/Login';
import {useNavigate} from "react-router-dom";
import AuthContext from "../contexts/AuthContext.tsx";
import CartContext from "../contexts/CartContext.tsx";
import OrderContext from "../contexts/OrderContext.tsx";
import {IOrder} from "../models/IOrder.ts";

const defaultOrder: IOrder = {
    items: [],
    shippingAddress: {
        name: '',
        street: '',
        address2: '',
        city: '',
        region: '',
        postalCode: '',
        country: '',
        email: '',
    },
    paymentMethod: 'Kreditkarte',
    totalPrice: 0,
    shippingCost: 0,
}

const supportedCurrencies = ['EUR', 'USD', 'GBP', 'CHF', 'CAD', 'AUD', 'JPY'];

const NavBar = () => {
    const {t} = useTranslation()
    const pages = [
        `${t('NAV_HOME_TEXT')}`,
        `${t('NAV_NECKLACES_TEXT')}`,
        `${t('NAV_BRACELETS_TEXT')}`,
        `${t('NAV_EARRINGS_TEXT')}`,
        `${t('NAV_RINGS_TEXT')}`,
        `${t("STERLING_SILVER")}`,
        `${t('STAINLESS_STEEL')}`,
        `${t('MYSTERY_BOXES_TEXT')}`,
        `${t('NAV_CUSTOMS_TEXT')}`
    ]
    const settings = [`${t('PROFILE_TEXT')}`, `${t('LOGOUT_TEXT')}`]
    const [anchorElNav, setAnchorElNav] = useState<null | HTMLElement>(null)
    const [anchorElUser, setAnchorElUser] = useState<null | HTMLElement>(null)
    const {isLoggedIn, logoutMutation} = useContext(AuthContext)
    const {selectedCurrency, setSelectedCurrency, setOrder} = useContext(OrderContext)
    const {cartItemCount} = useContext(CartContext)
    const navigate = useNavigate()

    const trigger = useScrollTrigger({
        threshold: 1,
    });

    const handleChange = (e: any) => {
        const value = e.target.value;
        setSelectedCurrency(value);
        localStorage.setItem('currency', value);
    };

    const handleOpenNavMenu = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorElNav(event.currentTarget);
    };
    const handleOpenUserMenu = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorElUser(event.currentTarget);
    };

    const handleCloseNavMenu = () => {
        setAnchorElNav(null);
    };

    const handleCloseUserMenu = () => {
        setAnchorElUser(null);
    };

    const handleClickMenu = (key: string) => {
        if (location.pathname.startsWith('/category/')) {
            const currentCategory = location.pathname.split('/category/')[1];
            if (currentCategory) {
                sessionStorage.setItem(`productsPageScroll${currentCategory}`, String(window.scrollY));
            }
        }

        switch (key) {
            case `${t('NAV_HOME_TEXT')}`:
                navigate('/')
                break
            case `${t('LOGIN_TEXT')}` :
                navigate('/login')
                break
            case `${t('LOGOUT_TEXT')}`:
                setOrder(defaultOrder)
                sessionStorage.clear()
                logoutMutation.mutate()
                break
            case `${t('PROFILE_TEXT')}`:
                navigate('/profile')
                break
            case `${t('NAV_NECKLACES_TEXT')}`:
                navigate('/category/halsketten')
                break
            case `${t('NAV_BRACELETS_TEXT')}`:
                navigate('/category/armbänder')
                break
            case `${t('MYSTERY_BOXES_TEXT')}`:
                navigate('/category/mysteryBoxen')
                break
            case `${t('NAV_EARRINGS_TEXT')}`:
                navigate('/category/ohrringe')
                break
            case `${t('NAV_RINGS_TEXT')}`:
                navigate('/category/ringe')
                break
            case `${t("STERLING_SILVER")}`:
                navigate('/category/sterlingSilber')
                break
            case `${t('STAINLESS_STEEL')}`:
                navigate('/category/stainlessSteel')
                break
            case `${t('NAV_CUSTOMS_TEXT')}`:
                navigate('/category/customs')
                break
            case `${t('NAV_ABOUT_ME_TEXT')}`:
                break
        }
        handleCloseNavMenu()
        handleCloseUserMenu()
    }

    return (
        <>
            <Slide appear={false} direction="down" in={!trigger}>
                <Box sx={{backgroundColor: 'primary.main'}}>
                    <Grid2 container padding={2}>
                        <Grid2 size="grow"/>
                        <Box>
                            <Link
                                sx={{cursor: 'pointer'}}
                                onClick={() => navigate('/')}
                            >
                                <Typography variant="h2"
                                            sx={{
                                                color: 'white',
                                                textAlign: 'center',
                                                fontFamily: 'Comic Sans MS, Comic Sans, cursive'
                                            }}
                                >
                                    NinaKsCreations
                                </Typography>
                            </Link>
                        </Box>
                        <Grid2 size="grow"/>
                    </Grid2>
                </Box>
            </Slide>
            <AppBar sx={{backgroundColor: 'primary.main'}}>
                <Toolbar>
                    <Box sx={{flexGrow: 1, display: {xs: 'flex', md: 'flex', lg: 'none'}}}>
                        <Tooltip title={t("OPEN_MENU")}>
                            <IconButton
                                onClick={handleOpenNavMenu}
                                color="inherit"
                            >
                                <MenuIcon/>
                            </IconButton>
                        </Tooltip>
                        <Menu
                            id="menu-appbar"
                            anchorEl={anchorElNav}
                            open={Boolean(anchorElNav)}
                            onClose={handleCloseNavMenu}

                            sx={{display: {xs: 'block', md: 'block', lg: 'none'}, left: -50, top: 5}}
                        >
                            {pages.map((page) => (
                                <MenuItem
                                    key={page}
                                    onClick={() => {
                                        handleClickMenu(page)
                                    }}
                                >
                                    <Typography sx={{textAlign: 'center'}}>{page}</Typography>
                                </MenuItem>
                            ))}
                        </Menu>
                    </Box>
                    <Box sx={{flexGrow: 1}}/>
                    <Box sx={{flexGrow: 1, display: {xs: 'none', md: 'none', lg: 'flex'}}}>
                        {pages.map((page) => (
                            <Box key={page} paddingLeft={4} paddingRight={4}>
                                <Button
                                    onClick={() => {
                                        handleClickMenu(page)
                                    }}
                                    sx={{color: 'white'}}
                                >
                                    <Typography>{page}</Typography>
                                </Button>
                            </Box>
                        ))}
                        {isLoggedIn ? (
                                <Box sx={{flexGrow: 0, display: {xs: 'none', md: 'none', lg: 'flex'}}}>
                                    <Tooltip title={t('PROFILE_TEXT')}>
                                        <IconButton color={'inherit'} onClick={handleOpenUserMenu} sx={{p: 0}}>
                                            <PersonIcon/>
                                        </IconButton>
                                    </Tooltip>
                                    <Menu
                                        sx={{mt: '45px', position: 'absolute', left: 16, top: -32}}
                                        id="menu-appbar"
                                        anchorEl={anchorElUser}
                                        open={Boolean(anchorElUser)}
                                        onClose={handleCloseUserMenu}
                                    >
                                        {settings.map((setting) => (
                                            <MenuItem key={setting} onClick={() => handleClickMenu(setting)}>
                                                <Typography sx={{textAlign: 'center'}}>{setting}</Typography>
                                            </MenuItem>
                                        ))}
                                    </Menu>
                                </Box>) :
                            <Tooltip title={t('LOGIN_TEXT')}>
                                <IconButton color={'inherit'} onClick={() => handleClickMenu('Login')} sx={{p: 0}}>
                                    <LoginIcon/>
                                </IconButton>
                            </Tooltip>
                        }
                        <Tooltip title={t('CART_TEXT')}>
                            <IconButton color={'inherit'} sx={{p: 0}} onClick={() => navigate('/cart')}>
                                <Badge badgeContent={cartItemCount} color='secondary'>
                                    <ShoppingCartIcon/>
                                </Badge>
                            </IconButton>
                        </Tooltip>
                        <FormControl size="small" sx={{
                            '& .MuiInputLabel-root': {color: 'white'}, // label text
                            '& .MuiOutlinedInput-root': {
                                '& fieldset': {borderColor: 'white'}, // default border
                                '&:hover fieldset': {borderColor: 'white'}, // hover border
                                '&.Mui-focused fieldset': {borderColor: 'white'}, // focused border
                            },
                            '& .MuiSelect-root': {color: 'white'}, // selected value
                            '& .MuiSvgIcon-root': {color: 'white'}, // dropdown arrow
                            '& .MuiSelect-select': {color: 'white',}
                        }}>
                            <InputLabel id="currency-label">{t('CURRENCY_TEXT')}</InputLabel>
                            <Select
                                labelId="currency-label"
                                value={selectedCurrency}
                                onChange={handleChange}
                                label="Currency"
                            >
                                {supportedCurrencies.map((code) => (
                                    <MenuItem key={code} value={code}>
                                        {code}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Box>
                    {isLoggedIn ? (
                            <Box sx={{flexGrow: 0, display: {xs: 'flex', md: 'flex', lg: 'none'}}}>
                                <Tooltip title={t('PROFILE_TEXT')}>
                                    <IconButton color={'inherit'}
                                                onClick={handleOpenUserMenu} sx={{p: 0}}>
                                        <PersonIcon/>
                                    </IconButton>
                                </Tooltip>
                                <Menu
                                    sx={{mt: '45px', position: 'absolute', left: 16, top: -32}}
                                    id="menu-appbar"
                                    anchorEl={anchorElUser}
                                    open={Boolean(anchorElUser)}
                                    onClose={handleCloseUserMenu}
                                >
                                    {settings.map((setting) => (
                                        <MenuItem key={setting} onClick={() => handleClickMenu(setting)}>
                                            <Typography sx={{textAlign: 'center'}}>{setting}</Typography>
                                        </MenuItem>
                                    ))}
                                </Menu>
                                <Tooltip title={t('CART_TEXT')}>
                                    <IconButton color={'inherit'} sx={{p: 0}} onClick={() => navigate('/cart')}>
                                        <Badge badgeContent={cartItemCount} color='secondary'>
                                            <ShoppingCartIcon/>
                                        </Badge>
                                    </IconButton>
                                </Tooltip>
                                <FormControl size="small" sx={{
                                    '& .MuiInputLabel-root': {color: 'white'}, // label text
                                    '& .MuiOutlinedInput-root': {
                                        '& fieldset': {borderColor: 'white'}, // default border
                                        '&:hover fieldset': {borderColor: 'white'}, // hover border
                                        '&.Mui-focused fieldset': {borderColor: 'white'}, // focused border
                                    },
                                    '& .MuiSelect-root': {color: 'white'}, // selected value
                                    '& .MuiSvgIcon-root': {color: 'white'}, // dropdown arrow
                                    '& .MuiSelect-select': {color: 'white',}
                                }}>
                                    <InputLabel id="currency-label">{t('CURRENCY_TEXT')}</InputLabel>
                                    <Select
                                        labelId="currency-label"
                                        value={selectedCurrency}
                                        onChange={handleChange}
                                        label="Currency"
                                    >
                                        {supportedCurrencies.map((code) => (
                                            <MenuItem key={code} value={code}>
                                                {code}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Box>
                        ) :
                        <Box sx={{flexGrow: 0, display: {xs: 'flex', md: 'flex', lg: 'none'}}}>
                            <Tooltip title="Login">
                                <IconButton color={'inherit'} onClick={() => handleClickMenu('Login')} sx={{p: 0}}>
                                    <LoginIcon/>
                                </IconButton>
                            </Tooltip>
                            <Tooltip title={t('CART_TEXT')}>
                                <IconButton color={'inherit'} sx={{p: 0}} onClick={() => navigate('/cart')}>
                                    <Badge badgeContent={cartItemCount} color='secondary'>
                                        <ShoppingCartIcon/>
                                    </Badge>
                                </IconButton>
                            </Tooltip>
                            <FormControl size="small" sx={{
                                '& .MuiInputLabel-root': {color: 'white'}, // label text
                                '& .MuiOutlinedInput-root': {
                                    '& fieldset': {borderColor: 'white'}, // default border
                                    '&:hover fieldset': {borderColor: 'white'}, // hover border
                                    '&.Mui-focused fieldset': {borderColor: 'white'}, // focused border
                                },
                                '& .MuiSelect-root': {color: 'white'}, // selected value
                                '& .MuiSvgIcon-root': {color: 'white'}, // dropdown arrow
                                '& .MuiSelect-select': {color: 'white',}
                            }}>
                                <InputLabel id="currency-label">{t('CURRENCY_TEXT')}</InputLabel>
                                <Select
                                    labelId="currency-label"
                                    value={selectedCurrency}
                                    onChange={handleChange}
                                    label="Currency"
                                >
                                    {supportedCurrencies.map((code) => (
                                        <MenuItem key={code} value={code}>
                                            {code}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Box>
                    }
                </Toolbar>
            </AppBar>
        </>
    )
}

export default NavBar